import { getToken } from "utils/helper";

import type {
  GiftCardTypeExtendDate,
  GiftCardListResult,
  GiftCardListQuery,
  GiftCardStats,
  UserGiftCardKey,
  ManualAssignPayload,
  GiftcardLite,
} from "utils/types/GiftcardTypes";
import { apiSrcUrl } from "../ApiRoutes";
import { ApiService } from "../apiservices";
import { CustomApiResponse } from "utils/helper/ApiResponses";
import { ApiResponse } from "utils/types/API";

type ApiStatus = "success" | "fail";
interface ApiEnvelope<T> {
  status: ApiStatus;
  message: string;
  code: number;
  data: T | null;
}

function authHeaders() {
  const token = getToken();
  if (!token) throw new Error("Authorization token is not available");
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
}

function toQuery(params: Record<string, any>) {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") q.set(k, String(v));
  });
  return q.toString();
}

// Normalize one card into UI shape
function normalizeCard(raw: any): GiftCardTypeExtendDate {
  const fullname = raw.fullName ?? raw.fullname ?? "";
  const addedTime = raw.addedTime ?? raw.createdAt ?? new Date().toISOString();
  const lastModified = raw.lastModified ?? raw.updatedAt ?? addedTime;
  const cost = typeof raw.cost === "number" ? raw.cost : Number(raw.cost ?? 0);
  const status = raw.status || (raw.sold ? "sold" : "available");
  return {
    id: String(raw.id),
    fullname,
    productId: String(raw.productId),
    cost,
    code: String(raw.code ?? ""),
    isActive: raw.isActive !== undefined ? Boolean(raw.isActive) : true,

    // NEW FIELDS
    status: status,
    saleId: raw.saleId ?? undefined, // Use the new saleId

    addedTime: String(addedTime),
    lastModified: String(lastModified),
  };
}

/** LIST (server-side pagination/sort/filter/search) */
export async function fetchGiftCardsAPI(
  query: GiftCardListQuery = {}
): Promise<GiftCardListResult> {
  // clamp sortBy to server-supported keys
  const allowedSort = new Set([
    "fullname",
    "productId",
    "sold",
    "addedTime",
    "lastModified",
  ]);
  const sortByParam = allowedSort.has(String(query.sortBy))
    ? query.sortBy
    : "addedTime";

  const qs = toQuery({
    page: query.page ?? 1,
    limit: query.limit ?? 40,
    search: query.search,
    sortBy: sortByParam,
    order: query.order ?? "desc",
    category: (query as any).category,
    provider: (query as any).provider,
    country: (query as any).country,
    status: query.status,
    isActive: query.isActive,
    productId: query.productId,
    minCost: query.minCost,
    maxCost: query.maxCost,
    addedFrom: query.addedFrom,
    addedTo: query.addedTo,
    includeDecrypted: query.includeDecrypted ?? "true",
  });

  const url = `${apiSrcUrl}/private/giftcard?${qs}`;

  const { data: json } = await ApiService.get<ApiEnvelope<GiftCardListResult>>(
    url,
    authHeaders()
  );

  if (json.status !== "success" || !json.data) {
    throw new Error(json.message || "Failed to fetch gift cards");
  }

  const payload = json.data; // GiftCardListResult from backend
  const rawList = Array.isArray(payload?.data) ? payload.data : [];
  const meta = payload.meta;

  return {
    data: rawList.map(normalizeCard),
    meta: {
      page: Number(meta?.page ?? 1),
      limit: Number(meta?.limit ?? rawList.length ?? 0),
      total: Number(meta?.total ?? rawList.length ?? 0),
      totalPages: Number(meta?.totalPages ?? 1),
    },
  };
}

/** CREATE */
export async function createGiftCard(
  giftCard: Partial<GiftCardTypeExtendDate>
): Promise<{ id: string }> {
  const url = `${apiSrcUrl}/private/giftcard`;
  const { data: json } = await ApiService.post<ApiEnvelope<{ id: string }>>(
    url,
    giftCard,
    authHeaders()
  );
  if (json.status !== "success" || !json.data) {
    throw new Error(json.message || "Failed to create gift card");
  }
  return json.data;
}

/** UPDATE */
export async function updateGiftCard(
  giftCard: Partial<GiftCardTypeExtendDate> & { id: string }
): Promise<void> {
  const url = `${apiSrcUrl}/private/giftcard/${giftCard.id}`;
  const { data: json } = await ApiService.put<ApiEnvelope<{}>>(
    url,
    giftCard,
    authHeaders()
  );
  if (json.status !== "success") {
    throw new Error(json.message || "Failed to update gift card");
  }
}

/** DELETE */
export async function deleteGiftCard(id: string): Promise<void> {
  const url = `${apiSrcUrl}/private/giftcard/${id}`;
  const { data: json } = await ApiService.delete<ApiEnvelope<{}>>(
    url,
    authHeaders()
  );
  if (json.status !== "success") {
    throw new Error(json.message || "Failed to delete gift card");
  }
}

/** STATS */
export async function fetchGiftCardStatsAPI(
  query: GiftCardListQuery = {}
): Promise<GiftCardStats> {
  const qs = toQuery({
    search: query.search,
    sortBy: query.sortBy,
    order: query.order,
    status: query.status,
    isActive: query.isActive,
    productId: query.productId,
    minCost: query.minCost,
    maxCost: query.maxCost,
    addedFrom: query.addedFrom,
    addedTo: query.addedTo,
  });

  const url = `${apiSrcUrl}/private/giftcard/stats?${qs}`;
  const { data: json } = await ApiService.get<ApiEnvelope<GiftCardStats>>(
    url,
    authHeaders()
  );

  if (json.status !== "success" || !json.data) {
    throw new Error(json.message || "Failed to fetch gift card stats");
  }
  return json.data;
}

type KeysPayload =
  | { keys: UserGiftCardKey[]; total: number; page?: number; limit?: number } // paginated
  | UserGiftCardKey[]; // flat array

function isPaginatedPayload(data: KeysPayload | null): data is {
  keys: UserGiftCardKey[];
  total: number;
  page?: number;
  limit?: number;
} {
  return !!data && typeof (data as any).keys !== "undefined";
}

/** Unwrap any of:
 * 1) axios default:         { data: CustomApiResponse<T>, status: 200, ... }
 * 2) custom wrapped shape:  { status: 200, data: CustomApiResponse<T> }
 * 3) already-unwrapped:     CustomApiResponse<T>
 */
function unwrapApi<T>(raw: any): CustomApiResponse<T> {
  // already a CustomApiResponse?
  if (raw && typeof raw.status === "string" && "data" in raw)
    return raw as CustomApiResponse<T>;

  // axios default
  if (raw && raw.data && typeof raw.data.status === "string")
    return raw.data as CustomApiResponse<T>;

  // custom wrapper like { status:200, data:{ status:"success", ... } }
  if (raw && raw.data && raw.data.data && typeof raw.data.status === "string") {
    return raw.data as CustomApiResponse<T>;
  }
  if (raw && raw.status && raw.data && typeof raw.data.status === "string") {
    return raw.data as CustomApiResponse<T>;
  }

  throw new Error("Unexpected API response shape.");
}

export async function fetchUserKeysAPI(
  userId: string,
  options?: {
    page?: number;
    limit?: number;
    search?: string;
    sortBy?: string;
    order?: "asc" | "desc";
  }
): Promise<{
  keys: UserGiftCardKey[];
  total: number;
  page: number;
  limit: number;
}> {
  if (!userId) throw new Error("User ID is required");

  const params = new URLSearchParams();
  if (options?.page) params.append("page", String(options.page));
  if (options?.limit) params.append("limit", String(options.limit));
  if (options?.search) params.append("search", options.search);
  if (options?.sortBy) params.append("sortBy", options.sortBy);
  if (options?.order) params.append("order", options.order);

  const url = `${apiSrcUrl}/private/users/${userId}/keys${
    params.toString() ? `?${params}` : ""
  }`;

  // NOTE: don't type the generic too tightly; unwrap it yourself
  const res = await ApiService.get(url, authHeaders());
  const api = unwrapApi<KeysPayload>(res);

  if (api.status !== "success") {
    throw new Error(api.message || "Failed to fetch user keys");
  }

  const data = api.data ?? null;

  if (isPaginatedPayload(data)) {
    return {
      keys: data.keys || [],
      total:
        typeof data.total === "number" ? data.total : data.keys?.length || 0,
      page: typeof data.page === "number" ? data.page : options?.page || 1,
      limit: typeof data.limit === "number" ? data.limit : options?.limit || 25,
    };
  }

  const keys = Array.isArray(data) ? data : [];
  return {
    keys,
    total: keys.length,
    page: options?.page || 1,
    limit: options?.limit || 25,
  };
}

export async function writeOffGiftCard<T>(
  cardId: string,
  reason: "expired" | "invalid" | "lost",
  token: string
): Promise<T> {
  const response = await ApiService.post<ApiResponse<T>>(
    `${apiSrcUrl}/private/giftcard/${cardId}/write-off`,
    { reason },
    { Authorization: `Bearer ${token}` }
  );

  // If your ApiService returns the parsed response directly
  const result = response.data;

  if (result.status === "success") {
    return result.data;
  }

  throw new Error(result.message || "Failed to write off the card.");
}

export async function redeemGiftCard(code: string) {
  const token = getToken();
  const res = await ApiService.post<
    CustomApiResponse<{
      creditedUSD: number;
      redeemedAt: number;
      giftCardId: string;
    }>
  >(
    `${apiSrcUrl}/private/giftcard/redeem`,
    { code },
    { Authorization: `Bearer ${token}` }
  );
  return res.data;
}

export async function manualAssignGiftcard(
  userId: string,
  payload: ManualAssignPayload
) {
  const token = getToken();
  if (!token) throw new Error("Token manquant");

  const url = `${apiSrcUrl}/private/users/${userId}/manual-assign-giftcard`;
  const res = await ApiService.post<
    CustomApiResponse<{
      saleId: string;
      charged: number;
      giftCardId: string;
      userId: string;
    }>
  >(url, payload, { Authorization: `Bearer ${token}` });

  return res.data;
}


export async function searchAvailableGiftcards(q: string, page = 1, limit = 10) {
  const token = getToken();
  const url = `${apiSrcUrl}/private/giftcard?status=available&search=${encodeURIComponent(q)}&page=${page}&limit=${limit}&order=asc`;
  const { data } = await ApiService.get<
    CustomApiResponse<{ data: GiftcardLite[]; meta: any }>
  >(url, { Authorization: `Bearer ${token}` });
  return data;
}
